/**
 * @graphql cc
 */
import { graphql, useFragment } from 'react-relay';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/button';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { Grid } from '@atlaskit/primitives/grid';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Inline from '@atlaskit/primitives/inline';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';

import { SmartCreateFeedbackVariant } from '@atlassian/smart-create-feedback/types';
import { AILabsLozenge } from '@atlassian/ai-components/ai-labs-lozenge';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	getTraceIdForContent,
	clearTraceIdForContent,
} from '@confluence/storage-manager/entry-points/aifcTraceIdStore.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { LoadableLazy, useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { usePageContentId } from '@confluence/page-context';
import { useCreationFocusedNavLand } from '@confluence/experiment-cfind-4473-creation-focused-nav/entry-points/useCreationFocusedNavLand.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { fg } from '@confluence/feature-gating';

const SmartCreateFeedbackLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SmartCreateFeedback" */ '@atlassian/smart-create-feedback'
			)
		).SmartCreateFeedback,
	name: 'SmartCreateFeedback',
});

import type {
	SmartCreateFeedbackBannerQuery$data,
	SmartCreateFeedbackBannerQuery$variables,
} from './__generated__/SmartCreateFeedbackBannerQuery.graphql.ts';
import type { SmartCreateFeedbackBanner_content$key } from './__generated__/SmartCreateFeedbackBanner_content.graphql.ts';
import { isBannerSeen, markBannerSeen, STORAGE_KEYS } from './bannerDismissal.ts';

const feedbackBannerStyles = xcss({
	padding: 'space.050',
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	backgroundColor: 'elevation.surface.sunken',
	color: 'color.text.subtle',
});

const feedbackBannerTopBorderStyles = xcss({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
});

const feedbackBannerLayerStyles = xcss({
	zIndex: 'modal',
});

const feedbackBannerMessageStyles = xcss({
	gridArea: 'message',
});

const closeButtonStyles = xcss({
	display: 'flex',
	justifySelf: 'end',
	gridArea: 'close',
});

const bannerStyles = xcss({
	width: '100%',
});

const i18n = defineMessages({
	bannerMessage: {
		id: 'editor-action-bar.feedback-banner.message.smart-create',
		defaultMessage:
			'This content was created with Rovo! We’d love to hear what you think. <link>Share feedback</link>',
		description: 'Banner message for the smart create feedback banner',
	},
	bannerMessageSlides: {
		id: 'editor-action-bar.feedback-banner.message.smart-create.slides',
		defaultMessage: 'Enjoying slides? Let us know what you think. <link>Share feedback</link>',
		description: 'Banner message for the smart create feedback banner on slides',
	},
	bannerClose: {
		id: 'editor-action-bar.feedback-banner.close.smart-create',
		defaultMessage: 'Close',
		description: 'Close button for the smart create feedback banner',
	},
});

const SmartCreateFeedbackBannerContentFragment = graphql`
	fragment SmartCreateFeedbackBanner_content on Content {
		title
		id
		type
		creatorId
		version {
			number
		}
		history {
			createdDate
		}
		contentProperties {
			draft {
				generatedBy
			}
			latest {
				generatedBy
			}
		}
	}
`;

const SmartCreateFeedbackBannerQuery = graphql`
	query SmartCreateFeedbackBannerQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			...SmartCreateFeedbackBanner_content @relay(mask: false)
		}
	}
`;

export type SmartCreateFeedbackBannerProps = {
	contentType: 'page' | 'whiteboard' | 'slide';
	contentFragmentRef?: SmartCreateFeedbackBanner_content$key | null;
};

const SmartCreateFeedbackBannerFragmentReader = ({
	contentType,
	contentFragmentRef,
}: SmartCreateFeedbackBannerProps & {
	contentFragmentRef: SmartCreateFeedbackBanner_content$key;
}) => {
	const content = useFragment(SmartCreateFeedbackBannerContentFragment, contentFragmentRef);
	return (
		<SmartCreateFeedbackBannerView
			contentType={contentType}
			singleContent={content}
			loading={false}
		/>
	);
};

const SmartCreateFeedbackBannerLegacy = ({ contentType }: SmartCreateFeedbackBannerProps) => {
	const [contentId] = usePageContentId();
	const { userId } = useSessionData();
	const { areBannersHidden } = useCreationFocusedNavLand();
	const { data, loading } = useQueryInterop<
		SmartCreateFeedbackBannerQuery$data,
		SmartCreateFeedbackBannerQuery$variables
	>(SmartCreateFeedbackBannerQuery, {
		variables: { contentId: contentId ?? '' },
		skip: !contentId || !userId || areBannersHidden,
	});
	return (
		<SmartCreateFeedbackBannerView
			contentType={contentType}
			singleContent={data?.singleContent}
			loading={loading}
		/>
	);
};

const SmartCreateFeedbackBannerComponent = ({
	contentType,
	contentFragmentRef,
}: SmartCreateFeedbackBannerProps) =>
	contentType === 'page' && contentFragmentRef ? (
		<SmartCreateFeedbackBannerFragmentReader
			contentType={contentType}
			contentFragmentRef={contentFragmentRef}
		/>
	) : (
		<SmartCreateFeedbackBannerLegacy contentType={contentType} />
	);

const SMART_CREATE_GENERATED_BY = 'ROVO_SMART_CREATION';
const ONE_HOUR_IN_MS = 60 * 60 * 1000;

const SmartCreateFeedbackBannerView = ({
	contentType,
	singleContent,
	loading,
}: Pick<SmartCreateFeedbackBannerProps, 'contentType'> & {
	singleContent: SmartCreateFeedbackBannerQuery$data['singleContent'] | undefined;
	loading: boolean;
}) => {
	const seenStorageKey = contentType === 'slide' ? STORAGE_KEYS.slidesSeen : STORAGE_KEYS.seen;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [contentId] = usePageContentId();
	const [showBanner, setShowBanner] = useState<boolean>(
		() => !(contentId && isBannerSeen(seenStorageKey, contentId)),
	);
	const [showFeedbackCollector, setShowFeedbackCollector] = useState<boolean>(false);
	// Snapshot once so the freshness check is stable across renders (avoids SSR/client mismatch
	// for content sitting near the 1-hour boundary).
	const [nowMs] = useState<number>(() => Date.now());
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	// Content the seen-check has run for, so SPA navigation re-checks a new page without re-hiding
	// the banner mid-view.
	const seenEvaluatedForRef = useRef<string | undefined>(undefined);
	const { userId, cloudId } = useSessionData();
	const pageMode = useGetPageMode();
	const contentMode = contentType === 'page' ? pageMode : undefined;

	const { areBannersHidden } = useCreationFocusedNavLand();

	useEffect(() => {
		if (contentId && seenEvaluatedForRef.current !== contentId) {
			seenEvaluatedForRef.current = contentId;
			setShowBanner(!isBannerSeen(seenStorageKey, contentId));
		}
	}, [contentId, seenStorageKey]);

	const handleOnClickClose = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'smartCreateFeedbackClose',
				source: 'smartCreateFeedbackBanner',
				attributes: {
					contentType,
					contentMode,
				},
			},
		}).fire();
		setShowBanner(false);
	}, [createAnalyticsEvent, contentType, contentMode]);

	const handleOnClickFeedbackButton = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'smartCreateFeedbackShare',
				source: 'smartCreateFeedbackBanner',
				attributes: {
					contentType,
					contentMode,
				},
			},
		}).fire();
		setShowFeedbackCollector(true);
	}, [createAnalyticsEvent, contentType, contentMode]);

	const contentProperties = singleContent?.contentProperties;

	const isGeneratedBySmartCreate =
		contentProperties?.draft?.generatedBy === SMART_CREATE_GENERATED_BY ||
		contentProperties?.latest?.generatedBy === SMART_CREATE_GENERATED_BY;

	const createdByCurrentUser = userId ? singleContent?.creatorId === userId : false;

	// Only show on freshly created content: first version, created within the last hour. Missing
	// version/date is treated as fresh so the banner isn't hidden when the server omits those fields.
	const isFirstVersion = (singleContent?.version?.number ?? 1) === 1;
	const createdDate = singleContent?.history?.createdDate;
	const createdWithinLastHour = createdDate
		? nowMs - new Date(createdDate).getTime() <= ONE_HOUR_IN_MS
		: true;

	const hiddenByFreshness =
		isGeneratedBySmartCreate && createdByCurrentUser && !(isFirstVersion && createdWithinLastHour);

	const shouldShowFeedbackBanner =
		isGeneratedBySmartCreate && createdByCurrentUser && !hiddenByFreshness;

	useEffect(() => {
		if (showBanner && shouldShowFeedbackBanner && !loading && !areBannersHidden) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'smartCreateFeedbackBanner',
					attributes: {
						contentType,
						contentMode,
					},
				},
			}).fire();
			// Record that the banner has been seen on this page so it won't show again here.
			if (contentId) {
				markBannerSeen(seenStorageKey, contentId);
			}
		}
	}, [
		showBanner,
		shouldShowFeedbackBanner,
		createAnalyticsEvent,
		contentType,
		contentMode,
		loading,
		areBannersHidden,
		contentId,
		seenStorageKey,
	]);

	// D0 experiment for simplifying the first day UX
	if (areBannersHidden || loading || !shouldShowFeedbackBanner) {
		return null;
	}

	const bannerMessage = contentType === 'slide' ? i18n.bannerMessageSlides : i18n.bannerMessage;

	return showBanner ? (
		<div data-vc="smart-create-feedback-banner" {...ssrPlaceholderIdProp} css={bannerStyles}>
			<Grid
				templateAreas={['empty message close']}
				templateColumns="1fr auto 1fr"
				xcss={[
					feedbackBannerStyles,
					feedbackBannerLayerStyles,
					(contentMode === 'view' || contentMode === 'live') && feedbackBannerTopBorderStyles,
				]}
				testId="smart-create-feedback-banner"
			>
				<Inline
					space="space.100"
					alignInline="center"
					alignBlock="center"
					xcss={feedbackBannerMessageStyles}
				>
					{!fg('aifc_onboarding_ga_enabled') ? (
						<AILabsLozenge />
					) : (
						!expValEquals('cc-aifc-databases-experiment', 'isEnabled', true) && <AILabsLozenge />
					)}
					<FormattedMessage
						{...bannerMessage}
						values={{
							link: (chunks: React.ReactNode[]) => (
								<Button appearance="link" spacing="none" onClick={handleOnClickFeedbackButton}>
									{chunks}
								</Button>
							),
						}}
					/>
				</Inline>
				<Box xcss={closeButtonStyles}>
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={CrossIcon}
						label={formatMessage(i18n.bannerClose)}
						onClick={handleOnClickClose}
					/>
				</Box>
			</Grid>

			{showFeedbackCollector && (
				<SmartCreateFeedbackLoader
					variant={
						contentType === 'slide'
							? SmartCreateFeedbackVariant.SLIDES
							: SmartCreateFeedbackVariant.CREATE_WITH_ROVO
					}
					onClose={() => {
						clearTraceIdForContent(contentId);
						setShowFeedbackCollector(false);
					}}
					onSubmit={() => {
						clearTraceIdForContent(contentId);
						setShowFeedbackCollector(false);
					}}
					tenantId={cloudId}
					additionalFields={{
						'Entry Point': 'Confluence SmartCreateFeedbackBanner',
						contentType,
						contentMode,
						traceId: getTraceIdForContent(contentId),
					}}
				/>
			)}
		</div>
	) : null;
};

export const SmartCreateFeedbackBanner: React.ComponentType<SmartCreateFeedbackBannerProps> =
	withFlags(SmartCreateFeedbackBannerComponent);
